import React from "react";
import "../DashboardStyles.css";

const SWOT = [
  {
    key: "strengths", label: "STRENGTHS",
    items: [
      "Dominant position in U.S. dry edible bean and sugar beet production",
      "Deep, productive soils and dual water access (surface + Ogallala) in the river valley",
      "Regional West Medical Center: Level II Trauma Center and major employer",
      "Six Critical Access Hospitals anchoring outlying county economies",
      "WNCC and Chadron State College provide a regional workforce pipeline",
      "National Park Service assets (Scotts Bluff, Agate, Chimney Rock) drive year-round visitation",
      "BFF commercial airport connecting the region to the national air network",
      "Over 50% of Nebraska’s oil and natural gas production originates in the region",
      "Strong wind and solar resource base (4th nationally for wind potential)",
      "BNSF mainline providing reliable, high-capacity freight access",
      "Low cost of living relative to Front Range Colorado and Black Hills competitors",
      "Active NRD system providing groundwater governance",
      "Strong agricultural identity and community cohesion",
    ],
  },
  {
    key: "weaknesses", label: "WEAKNESSES",
    items: [
      "Median household income $13,800 below the state average",
      "Bachelor’s degree attainment 10.5 points below the state rate",
      "Population decline across 9 of 11 counties since 2010; frontier density in 6 counties",
      "Broadband: 12–15% of locations statewide unserved/underserved; rural Panhandle disproportionately affected",
      "Workforce housing shortage constraining employer recruitment across sectors",
      "Agricultural employment declining with mechanization; limited private-sector diversification",
      "Healthcare workforce shortages: whole-county primary care HPSAs in 5 counties",
      "High property tax burden on agricultural landowners due to a thin commercial tax base",
      "Seasonal, weather-dependent tourism with limited shoulder-season offerings",
      "No interstate highway access in northern Panhandle counties",
      "Thin labor market makes large-scale industrial recruitment difficult",
      "9.5% uninsured rate exceeds the state average of 7.1%",
      "Long distances to specialist healthcare, universities, and major commercial centers",
    ],
  },
  {
    key: "opportunities", label: "OPPORTUNITIES",
    items: [
      "BEAD deployment covering 14,032 eligible locations statewide — Panhandle communities remain eligible in ongoing and gap-fill funding rounds; broadband connectivity is enabling infrastructure for precision ag, telehealth, and remote work",
      "Nebraska Passport Program and ‘Nebraska. All Heart.’ create a statewide marketing platform",
      "IRA and IIJA transmission and clean-energy funding could unlock wind/solar at scale",
      "Growing national demand for domestic food production; specialty crops benefit from trade interest",
      "Remote work economy enables talent attraction to low-cost, high-amenity communities",
      "Regional West expansion and telehealth buildout can extend healthcare reach",
      "Value-added agriculture processing can capture more value locally",
      "HPSA/MUA designations create federal recruitment incentives (NHSC loan repayment, J-1 waivers)",
      "Agritourism and hunting/fishing economy underdeveloped relative to the asset base",
      "Bridgeport viaduct and US-385/US-26 corridor investments improve freight efficiency",
      "Growing nuclear-energy interest may revive uranium exploration in Dawes County",
      "Cross-border trade with Wyoming and Colorado creates retail and services opportunity",
    ],
  },
  {
    key: "threats", label: "THREATS",
    items: [
      "Continued out-migration of working-age adults and college-educated youth erodes the economic base",
      "Ogallala Aquifer depletion threatens long-term irrigated agriculture viability",
      "Agricultural commodity price volatility creates cyclical income instability",
      "Federal agriculture policy shifts affect farm income directly",
      "Coal plant retirements reduce BNSF freight volumes and railroad employment over time",
      "CAH financial fragility: declining reimbursement and persistent provider shortages",
      "Climate variability: drought frequency and severity increasing in the High Plains",
      "Property tax burden on agricultural land continues to intensify",
      "Competition from larger metros for healthcare workers, educators, and technical professionals",
      "Potential federal discretionary spending cuts affecting NPS, USFS, BLM, and rural programs",
      "Trade policy uncertainty affecting export markets for corn, beef, and dry edible beans",
      "Wind-development opposition in some communities could foreclose lease and tax revenue",
    ],
  },
];

export default function Chapter8Dashboard() {
  return (
    <div className="chapter chapter8">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 8</p>
        <h2>Regional SWOT Analysis</h2>
        <p className="chapter-lede">
          Strengths, weaknesses, opportunities, and threats: an honest map of
          the terrain from which the next five years of strategy are drawn.
        </p>

        <h3>8.1 Introduction</h3>
        <p>
          The Regional SWOT Analysis synthesizes the findings of Chapters 2
          through 7 into a structured strategic framework that bridges the
          evidence base and the action plan. It is designed to answer a single
          question for the reader arriving at Chapter 9: given everything we
          know about this region (its people, its assets, its infrastructure,
          its economy, its quality of life, and its educational institutions)
          where should we concentrate our energy and resources for the next five
          years?
        </p>
        <p>
          Strengths and weaknesses reflect existing <strong>internal</strong>{" "}
          conditions of the region — assets and capabilities the Panhandle can
          build on, and structural deficiencies it must work to address.
          Opportunities and threats reflect <strong>external</strong> forces and
          trends to which the region must respond. The SWOT is not a prediction.
          It is an honest map of the terrain from which strategic decisions are
          made.
        </p>

        <div className="swot-grid">
          {SWOT.map((q) => (
            <div key={q.key} className={`swot-card ${q.key}`}>
              <h4>{q.label}</h4>
              <ul>
                {q.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <p className="source-note">
          Source Note: SWOT synthesizes findings from Chapters 2–7. Sources
          include ACS 5-Year Estimates, USDA NASS, NPS visitor spending analysis,
          NDOT STIP, FAA Airport Master Record, Nebraska Broadband Office BEAD
          Five-Year Action Plan, FHWA National Bridge Inventory, HRSA HPSA data,
          CMS Critical Access Hospital data, Nebraska Travel Association (Feb
          2026), and BEA tourism GDP data.
        </p>

        <h3>8.1a What’s Changed Since 2021</h3>
        <p>
          This is not the Panhandle’s first SWOT analysis. PADD’s 2021 CEDS
          produced its own regional SWOT, and reading the two side by side across
          a five-year interim reveals something rare in regional planning: a
          consistent, accurate diagnosis that simply lacked, until now, the
          evidence base and action structure to move on it.
        </p>
        <p>
          Several 2021 findings carry forward almost unchanged, not because the
          region failed to act, but because the underlying conditions are
          structural and slow-moving. “Not attracting or retaining young
          workforce” was a 2021 weakness; this CEDS now has the data to say
          precisely how much (23 percent of teens likely to stay, a measurable
          labor-force contraction in every county, Chapter 5.4) and a Strategy
          Council validating it as the top-rated “serious” concern in the 2026
          community survey. “Lincoln and Omaha decision-making removed from the
          Panhandle” was a 2021 threat; it is now the organizing argument of
          this entire CEDS — that the Panhandle’s central risk is being bypassed
          as growth concentrates in eastern Nebraska.
        </p>
        <p>
          Other 2021 findings have moved from observation to action. “Helping
          farmers develop value-added agricultural products” sat in the 2021
          SWOT as an opportunity; in this cycle, Strategy Council applicants
          independently raised the same point with enough specificity and
          conviction that value-added agriculture is now one of the six target
          sectors in the Panhandle Rural Economy Diversification Strategy under
          Objective 4.2 (Chapter 9.4), with an assigned lead and a Year 1
          action. “Getting young people engaged in the community... especially
          in the conversations around succession” was a 2021 opportunity; it is
          now Objective 2.1’s Business Succession Registry, with a 90-day service
          standard and a five-year target of 15 completed transitions. “Career
          ladders starting with the high school vocational programs” is now the
          employer-linked apprenticeship strategy in Objective 2.2.
          “Territorialism and refusal to collaborate” — named as a 2021 threat —
          is the precise problem this CEDS’s regional data-clearinghouse and
          coordination strategies (Goal 4.1) are built to address.
        </p>
        <p>
          One 2021 weakness has moved further than the others: “lack of mental
          health resources” sat as a bare assertion in both the 2021 SWOT and,
          until this revision, in this CEDS’s own Chapter 6. This cycle replaces
          that assertion with the region’s actual Community Health Assessment
          findings (mental health is the top-ranked community health concern in
          the Panhandle, ahead of cancer and poverty) and adds a tracked KPI
          under Goal 3’s broadband objective connecting telehealth-capable
          broadband to behavioral-health access specifically. This CEDS does not
          propose a standalone behavioral-health service objective in this
          cycle; the access gap is now measured and tied to an infrastructure
          lever this CEDS already advances, not yet addressed by a dedicated
          strategy of its own. One 2021 weakness remains without a clear 2026
          counterpart and is worth the Strategy Council’s attention before this
          plan is finalized: “no mechanism to easily support unemployment
          applicants.”
        </p>
        <p>
          The throughline across both cycles is not a region in decline,
          repeating the same complaints without progress. It is a region whose
          own people have correctly identified its central challenges for at
          least five years. The difference in 2026 is not the diagnosis, but the
          infrastructure now built to act on it: measurable targets, named
          leads, and an annual review.
        </p>
        <p className="source-note">
          Source note: 2021 SWOT findings drawn from PADD’s 2021 Comprehensive
          Economic Development Strategy, Chapter 8.
        </p>

        <h3>8.2 Strategic Implications</h3>
        <p>
          Several cross-cutting implications emerge from the SWOT that directly
          inform the four goals of the Regional Action Plan.
        </p>

        <h4>Internal vulnerabilities are prerequisites, not add-ons</h4>
        <p>
          The region’s most urgent internal vulnerabilities — housing shortage,
          childcare gaps, workforce aging, and healthcare provider shortage — are
          all conditions that, if unaddressed, will accelerate the population
          decline that drives every other structural challenge. Goal 1
          (Foundations) and Goal 2 (Workforce) are direct responses. They are
          not aspirational additions to a plan built around industry recruitment
          — they are the prerequisite investments without which recruitment
          cannot succeed.
        </p>

        <h4>The biggest external opportunities are time-sensitive</h4>
        <p>
          IRA and IIJA energy investment, the Nebraska Passport Program and
          ‘Nebraska. All Heart.’ campaign, and the remote-work economy share one
          characteristic: they reward early, sustained action. IRA transmission
          incentives are subject to policy change. The strategic implication is
          urgency on energy and talent attraction: the Panhandle must act
          decisively on these in the <strong>first two years</strong> of the
          planning period, not the last. BEAD broadband is the exception. It has
          shifted from a defined application window to an ongoing monitoring
          posture (Chapter 5.8), and PADD’s role is sustained advocacy in
          Nebraska’s gap-fill rebidding and in decisions on unallocated funds.
        </p>

        <h4>The most serious threats require long-term management</h4>
        <p>
          Ogallala Aquifer depletion, CAH financial fragility, continued
          out-migration, and fiscal stress from an agricultural-dominated
          property tax base all require deliberate long-term management rather
          than short-term crisis response. Goal 4 (Regional Capacity,
          Accountability &amp; Resilience) is specifically designed to build the
          institutional capacity and natural-resource management frameworks
          needed to manage these threats over the full planning horizon and
          beyond.
        </p>
        <p>
          The combination of significant natural assets, nationally significant
          federal designations, and a proven regional development institution in
          PADD gives the Panhandle a stronger platform than its raw demographic
          statistics might suggest. The SWOT does not depict a region in
          freefall — it depicts a region with genuine competitive advantages
          that have been underutilized, facing serious but addressable
          structural challenges. The Regional Action Plan in Chapter 9 is that
          strategy.
        </p>
      </div>
    </div>
  );
}
